import React, { useState, useEffect } from 'react';
import FileUpload from './components/FileUpload';
import ChatInterface from './components/ChatInterface';
import Login from './components/Login';
import './styles/App.css';

function App() {
  const [activeTab, setActiveTab] = useState('chat');
  const [totalChunks, setTotalChunks] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const response = await fetch('/api/auth/check', {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.authenticated) {
        setIsAuthenticated(true);
        setUser(data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setCheckingAuth(false);
    }
  };

  const handleLoginSuccess = (userEmail) => {
    setIsAuthenticated(true);
    setUser(userEmail);
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/logout', {
        method: 'POST',
        credentials: 'include'
      });
      setIsAuthenticated(false);
      setUser(null);
      setTotalChunks(0);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleUploadSuccess = (data) => {
    setTotalChunks(data.total_chunks);
    // Switch to chat tab after successful upload
    setTimeout(() => setActiveTab('chat'), 1500);
  };

  if (checkingAuth) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <h1>Partner Marketing Agent</h1>
          <p className="subtitle">AI-powered event recommendations for partner engagement</p>
        </div>
        <div className="header-right">
          {totalChunks > 0 && (
            <div className="kb-status">
              Knowledge Base: {totalChunks} chunks
            </div>
          )}
          <span className="user-email">👤 {user}</span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          Chat
        </button>
        <button
          className={`tab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          Upload Documents
        </button>
      </div>

      <main className="app-main">
        {activeTab === 'chat' && (
          <div className="tab-content">
            <ChatInterface apiUrl={apiUrl} />
          </div>
        )}

        {activeTab === 'upload' && (
          <div className="tab-content">
            <div className="upload-section">
              <h2>Build Knowledge Base</h2>
              <p className="section-description">
                Upload documents containing event information, schedules, and partner details.
                The agent will use this information to provide recommendations.
              </p>
              <FileUpload apiUrl={apiUrl} onUploadSuccess={handleUploadSuccess} />

              <div className="upload-tips">
                <h3>Tips for best results:</h3>
                <ul>
                  <li>Include event dates, locations, and industry focus</li>
                  <li>Mention partner companies and their participation</li>
                  <li>Note technology themes and target audiences</li>
                  <li>Add information about Elastic's involvement (or lack thereof)</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <p>Powered by Claude AI • Built on AWS</p>
      </footer>
    </div>
  );
}

export default App;
